import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Table,
  Space,
  Tooltip,
  Tag,
  Popconfirm,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../../configs";
import dayjs from "dayjs";
import "dayjs/locale/id";
import axios from "axios";

const { Title, Text } = Typography;

const PaymentReceived = () => {
  const history = useHistory();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const response = await axios.get("/finance/payment-received", {
        params: {
          page: params.current || pagination.current,
          size: params.pageSize || pagination.pageSize,
          search: searchText,
          ...params,
        },
      });
      const { content, totalElements } = response.data.data;
      setData(content);
      setPagination({
        ...pagination,
        current: response.data.data.current,
        total: totalElements,
      });
    } catch (error) {
      console.error(error);
      message.error("Gagal mengambil data pembayaran.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleTableChange = (newPagination) => {
    fetchData({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/finance/payment-received/${id}`);
      message.success("Data berhasil dihapus");
      fetchData();
    } catch (error) {
      console.error(error);
      message.error("Gagal menghapus data");
    }
  };

  const columns = [
    {
      title: "NO.",
      key: "index",
      width: 50,
      align: "center",
      fixed: "left",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "TGL. PEMBAYARAN",
      dataIndex: "paymentDate",
      key: "paymentDate",
      width: 130,
      render: (text) =>
        text ? dayjs(text).locale("id").format("DD MMM YY") : "-",
    },
    {
      title: "PEMBELI",
      dataIndex: "idContact",
      key: "idContact",
      width: 150,
      ellipsis: true,
    },
    {
      title: "NO. PEMBAYARAN",
      dataIndex: "paymentNumber",
      key: "paymentNumber",
      width: 140,
      ellipsis: true,
    },
    {
      title: "NO. INVOICE",
      dataIndex: "idInvoice",
      key: "idInvoice",
      width: 120,
      responsive: ["md"],
      ellipsis: true,
    },
    {
      title: "METODE",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 100,
      responsive: ["lg"],
    },
    {
      title: "JUMLAH",
      dataIndex: "amount",
      key: "amount",
      width: 130,
      render: (value) =>
        new Intl.NumberFormat("id-ID", {
          style: "currency",
          currency: "IDR",
          minimumFractionDigits: 0,
        }).format(value),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      width: 80,
      responsive: ["sm"],
      render: (status) => {
        let color = "default";
        if (status === "Lunas") color = "success";
        if (status === "Pending") color = "warning";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "AKSI",
      key: "action",
      width: 90,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              size="small"
              type="primary"
              icon={<EditOutlined />}
              style={{
                background: "#3699FF",
                borderColor: "#3699FF",
              }}
              onClick={() =>
                history.push(
                  `/finance/payment-received/form/${record.idPaymentReceived}`,
                )
              }
            />
          </Tooltip>
          <Popconfirm
            title="Hapus data ini?"
            onConfirm={() => handleDelete(record.idPaymentReceived)}
            okText="Ya"
            cancelText="Batal"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Hapus">
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: "16px" }}>
      {/* Header Section */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={16}>
          <Title level={4} style={{ marginBottom: 4 }}>
            Daftar Penerimaan Pembayaran
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Kelola daftar penerimaan pembayaran dari pelanggan.
          </Text>
        </Col>
        <Col
          xs={24}
          md={8}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{
              background: "#1BC5BD",
              borderColor: "#1BC5BD",
              fontWeight: 600,
              width: "100%",
              maxWidth: 200,
            }}
            onClick={() => history.push("/finance/payment-received/form")}
          >
            Buat Baru
          </Button>
        </Col>
      </Row>

      {/* Search Section */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={10}>
          <Input
            placeholder="Cari No. Pembayaran..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={() => fetchData({ current: 1 })}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={24} sm={12} md={14}>
          <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setSearchText("");
                fetchData({ current: 1 });
              }}
            >
              Reset
            </Button>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => fetchData({ current: 1 })}
              style={{
                background: "#8950FC",
                borderColor: "#8950FC",
              }}
            >
              Cari
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Table with horizontal scroll for mobile */}
      <Table
        columns={columns}
        dataSource={data}
        rowKey="idPaymentReceived"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          size: "small",
          showTotal: (total) => `Total ${total} data`,
        }}
        loading={loading}
        onChange={handleTableChange}
        bordered
        size="small"
        scroll={{ x: 800 }}
      />
    </Card>
  );
};

export default PaymentReceived;
